<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useDatatablesStore } from '../store/index.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import PageEmptyState from 'escaleshop/shared/components/PageEmptyState.vue';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
const { isCloudFeatureEnabled, isOnChatwootCloud, accountId } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);
const isFlowEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.FLOW));

const router = useRouter();
const { accountScopedRoute } = useAccount();

const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};
const store = useDatatablesStore();

const searchQuery = ref('');
const deletingTable = ref(null);
const deleteDialogRef = ref(null);
const isDeleting = ref(false);
const openMenuSlug = ref(null);

onMounted(() => store.fetchTables());

const filteredTables = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return store.tables;
  return store.tables.filter(
    t => t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q)
  );
});

const tableMenuItems = computed(() => [
  { action: 'open', value: 'open', label: t('DATATABLES.INDEX.MENU.OPEN') },
  { action: 'delete', value: 'delete', label: t('DATATABLES.INDEX.MENU.DELETE') },
]);

function toggleMenu(slug) {
  openMenuSlug.value = openMenuSlug.value === slug ? null : slug;
}

function closeMenu() {
  openMenuSlug.value = null;
}

function goToTable(table) {
  router.push(accountScopedRoute('datatables_show', { slug: table.slug }));
}

function goToNew() {
  router.push(accountScopedRoute('datatables_new'));
}

async function handleAction({ action }, table) {
  closeMenu();
  if (action === 'open') {
    goToTable(table);
  } else if (action === 'delete') {
    deletingTable.value = table;
    await nextTick();
    deleteDialogRef.value?.open();
  }
}

function onDeleteClose() {
  deletingTable.value = null;
}

async function confirmDelete() {
  if (!deletingTable.value) return;
  isDeleting.value = true;
  try {
    await store.deleteTable(deletingTable.value.slug);
    deleteDialogRef.value?.close();
    useAlert(t('DATATABLES.INDEX.DELETE.SUCCESS'));
  } catch {
    useAlert(t('DATATABLES.INDEX.DELETE.ERROR'));
  } finally {
    isDeleting.value = false;
  }
}
</script>

<template>
  <div class="flex w-full h-full flex-col overflow-hidden bg-n-surface-1">
  <template v-if="!isFlowEnabled">
    <div class="flex flex-1 items-center justify-center min-h-[400px] px-6 py-12">
      <BasePaywallModal
        feature-prefix="FLOW"
        :i18n-key="paywallI18nKey"
        :is-super-admin="isSuperAdmin"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        @upgrade="openBilling"
      />
    </div>
  </template>
  <template v-else>

    <header class="sticky top-0 z-10 px-6">
      <div class="flex items-start sm:items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl">
        <span class="text-xl font-medium truncate text-n-slate-12">{{ t('DATATABLES.INDEX.TITLE') }}</span>
        <div class="flex items-center flex-shrink-0 gap-4">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('DATATABLES.INDEX.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
          <Button :label="t('DATATABLES.INDEX.NEW_TABLE')" size="sm" class="flex-shrink-0" @click="goToNew" />
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6 pb-6">
      <div class="w-full mx-auto max-w-5xl">

        <template v-if="store.loading">
          <div class="flex flex-col gap-4 animate-pulse">
            <div v-for="i in 3" :key="i" class="h-16 w-full bg-n-slate-3 rounded-xl" />
          </div>
        </template>

        <PageEmptyState
          v-else-if="!filteredTables.length && !searchQuery"
          icon="i-lucide-table"
          :title="t('DATATABLES.INDEX.EMPTY_TITLE')"
          :description="t('DATATABLES.INDEX.EMPTY_DESCRIPTION')"
        >
          <Button size="sm" icon="i-lucide-plus" @click="goToNew">
            {{ t('DATATABLES.INDEX.CREATE_TABLE') }}
          </Button>
        </PageEmptyState>

        <div v-else class="space-y-4">
          <div v-if="filteredTables.length === 0" class="py-8 text-center text-sm text-n-slate-10">
            {{ t('DATATABLES.INDEX.NO_RESULTS') }}
          </div>
          <div v-for="table in filteredTables" :key="table.slug">
            <CardLayout layout="row">
              <!-- Left: name + description -->
              <div
                class="flex flex-col gap-0.5 min-w-0 flex-1 cursor-pointer"
                @click="goToTable(table)"
              >
                <span class="text-sm font-medium text-n-slate-12 line-clamp-1 hover:text-n-brand transition-colors">
                  {{ table.name }}
                </span>
                <span v-if="table.description" class="text-xs text-n-slate-10 line-clamp-1">
                  {{ table.description }}
                </span>
              </div>

              <!-- Right: stats + menu -->
              <div class="flex items-center justify-end gap-4 flex-shrink-0">
                <div class="flex items-center gap-4">
                  <span class="text-sm text-n-slate-11 whitespace-nowrap">
                    {{ t('DATATABLES.INDEX.COLUMNS_COUNT', table.schema?.length ?? 0) }}
                  </span>
                  <div class="w-px h-3 bg-n-weak" />
                  <span class="text-sm text-n-slate-11 whitespace-nowrap">
                    {{ t('DATATABLES.INDEX.ROWS_COUNT', table.rows_count ?? 0) }}
                  </span>
                </div>

                <div
                  v-on-clickaway="closeMenu"
                  class="relative group"
                >
                  <Button
                    icon="i-lucide-ellipsis-vertical"
                    color="slate"
                    size="xs"
                    class="rounded-md group-hover:bg-n-alpha-2"
                    @click.stop="toggleMenu(table.slug)"
                  />
                  <DropdownMenu
                    v-if="openMenuSlug === table.slug"
                    :menu-items="tableMenuItems"
                    class="ltr:right-0 rtl:left-0 mt-1 top-full z-60 min-w-[140px]"
                    @action="handleAction($event, table)"
                  />
                </div>
              </div>
            </CardLayout>
          </div>
        </div>

      </div>
    </main>

  </template>
  </div>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('DATATABLES.INDEX.DELETE.TITLE')"
    :description="
      deletingTable
        ? t('DATATABLES.INDEX.DELETE.DESCRIPTION', { name: deletingTable.name })
        : ''
    "
    @close="onDeleteClose"
  >
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('DATATABLES.INDEX.DELETE.CANCEL')"
          class="w-full"
          :disabled="isDeleting"
          @click="deleteDialogRef?.close()"
        />
        <Button
          color="ruby"
          :label="t('DATATABLES.INDEX.DELETE.CONFIRM')"
          class="w-full"
          :is-loading="isDeleting"
          :disabled="isDeleting"
          @click="confirmDelete"
        />
      </div>
    </template>
  </Dialog>
</template>
